import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Users, Plus, Pencil, Trash2, Shield, ShieldCheck, X, Eye, EyeOff, Crown, UserCheck as UserCheckIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import Navigation from '../components/Navigation';
import { getAdminHeaders } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALL_PERMISSIONS = [
  { key: 'visualizacao', label: 'Visualização', desc: 'Ver páginas e informações' },
  { key: 'projeto_sementes', label: 'Projeto Sementes', desc: 'Pontuação, ranking, lançamentos' },
  { key: 'administracao', label: 'Administração', desc: 'Backups, dados, logs, usuários' },
  { key: 'edicao', label: 'Edição', desc: 'Criar, editar e excluir dados' },
];

const ROLES = [
  { key: 'admin', label: 'Administrador', icon: Crown, color: 'bg-[#1A4D2E]', defaultPerms: ['visualizacao', 'projeto_sementes', 'administracao', 'edicao'] },
  { key: 'lider', label: 'Líder', icon: ShieldCheck, color: 'bg-blue-600', defaultPerms: ['visualizacao', 'projeto_sementes', 'edicao'] },
  { key: 'voluntario', label: 'Voluntário', icon: Shield, color: 'bg-[#5A7A3B]', defaultPerms: ['visualizacao'] },
  { key: 'responsavel', label: 'Pais/Responsável', icon: UserCheckIcon, color: 'bg-amber-600', defaultPerms: ['visualizacao', 'projeto_sementes'] },
];

const GestaoUsuarios = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ nome: '', email: '', celular: '', password: '', role: 'voluntario', permissions: [] });
  const [areaLabels, setAreaLabels] = useState({});
  const [approvedAreas, setApprovedAreas] = useState({}); // { userId: [areas] }

  useEffect(() => { fetchUsers(); fetchAreaLabels(); }, []);

  const fetchAreaLabels = async () => {
    try {
      const res = await axios.get(`${API}/volunteers/areas`);
      const map = {};
      (res.data || []).forEach(a => { map[a.value] = a.label; });
      setAreaLabels(map);
    } catch {}
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { headers: getAdminHeaders() });
      setUsers(res.data.users || []);
    } catch (e) { toast.error('Erro ao carregar usuários'); }
    try {
      const res = await axios.get(`${API}/admin/pending`, { headers: getAdminHeaders() });
      setPending(res.data.pending || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const resetForm = () => {
    setForm({ nome: '', email: '', celular: '', password: '', role: 'voluntario', permissions: ['visualizacao'] });
    setEditingUser(null);
    setShowForm(false);
    setShowPassword(false);
  };

  const handleRoleChange = (roleKey) => {
    const role = ROLES.find(r => r.key === roleKey);
    setForm(prev => ({ ...prev, role: roleKey, permissions: role?.defaultPerms || ['visualizacao'] }));
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setForm({
      nome: user.nome || '',
      email: user.email || '',
      celular: user.celular || '',
      password: '',
      role: user.role || 'voluntario',
      permissions: user.permissions || ['visualizacao']
    });
    setShowForm(true);
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const togglePermission = (key) => {
    setForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter(p => p !== key)
        : [...prev.permissions, key]
    }));
  };

  const selectAllPermissions = () => {
    setForm(prev => ({ ...prev, permissions: ALL_PERMISSIONS.map(p => p.key) }));
  };

  const clearAllPermissions = () => {
    setForm(prev => ({ ...prev, permissions: [] }));
  };

  const handleSubmit = async () => {
    if (!form.nome) { toast.error('Nome é obrigatório'); return; }
    if (!editingUser && !form.password) { toast.error('Senha é obrigatória'); return; }
    if (!editingUser && form.password.length < 6) { toast.error('Senha deve ter pelo menos 6 caracteres'); return; }

    try {
      if (editingUser) {
        const body = { nome: form.nome, email: form.email, celular: form.celular, role: form.role, permissions: form.permissions };
        if (form.password) body.password = form.password;
        await axios.put(`${API}/admin/users/${editingUser.id}`, body, { headers: getAdminHeaders() });
        toast.success('Usuário atualizado!');
      } else {
        await axios.post(`${API}/admin/create-user`, {
          nome: form.nome, email: form.email, celular: form.celular, password: form.password,
          role: form.role, permissions: form.permissions
        }, { headers: getAdminHeaders() });
        toast.success('Usuário criado!');
      }
      resetForm();
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar usuário');
    }
  };

  const handleDelete = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: getAdminHeaders() });
      toast.success('Usuário excluído!');
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao excluir'); }
  };

  const handleApprove = async (userId, nome, areas) => {
    const selectedAreas = approvedAreas[userId] || areas || [];
    try {
      await axios.post(`${API}/admin/approve/${userId}`, { approved_areas: selectedAreas }, { headers: getAdminHeaders() });
      toast.success(`${nome} aprovado!`);
      setApprovedAreas(prev => { const n = { ...prev }; delete n[userId]; return n; });
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao aprovar'); }
  };

  const handleReject = async (userId, nome) => {
    try {
      await axios.post(`${API}/admin/reject/${userId}`, {}, { headers: getAdminHeaders() });
      toast.success(`${nome} rejeitado`);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao rejeitar'); }
  };

  const toggleApprovedArea = (userId, area, allAreas) => {
    setApprovedAreas(prev => {
      const current = prev[userId] || [...allAreas];
      const updated = current.includes(area) ? current.filter(a => a !== area) : [...current, area];
      return { ...prev, [userId]: updated };
    });
  };

  if (loading) return <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center"><p className="text-gray-500">Carregando...</p></div>;

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      <div className="bg-[#1A4D2E] text-white px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <button onClick={() => navigate(-1)} className="hover:bg-white/10 rounded-full p-2 transition-all mb-2" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="w-7 h-7" />
              <h1 className="font-nunito font-bold text-3xl">Gestão de Usuários</h1>
            </div>
            <button onClick={openCreate} data-testid="add-user-btn" className="bg-white/10 hover:bg-white/20 text-white rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-all">
              <Plus className="w-4 h-4" /> Novo Usuário
            </button>
          </div>
          <p className="font-inter text-white/70 text-sm mt-1">
            {users.length} usuário(s) cadastrado(s)
            {pending.length > 0 && <span className="ml-2 bg-amber-500 text-white px-2 py-0.5 rounded-full text-xs font-bold">{pending.length} pendente(s)</span>}
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        {/* Pending Approvals */}
        {pending.length > 0 && (
          <Card className="bg-amber-50 rounded-2xl p-5 shadow-lg border-2 border-amber-200">
            <h3 className="font-nunito font-bold text-lg text-amber-800 mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5" /> Aguardando Aprovação ({pending.length})
            </h3>
            <div className="space-y-2">
              {pending.map(u => {
                const userAreas = u.areas || [];
                const hasMultipleAreas = userAreas.length > 1;
                const selectedForUser = approvedAreas[u.id] || [...userAreas];
                const selectedCount = selectedForUser.length;

                return (
                  <div key={u.id} className="bg-white rounded-xl p-4" data-testid={`pending-${u.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-nunito font-bold text-[#1A4D2E]">{u.nome || 'Sem nome'}</p>
                        <p className="font-inter text-xs text-gray-500">{u.celular || u.email || 'Sem contato'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleApprove(u.id, u.nome, userAreas)} data-testid={`approve-${u.id}`}
                          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-xs font-semibold transition-all">
                          {hasMultipleAreas && selectedCount < userAreas.length ? `Aprovar ${selectedCount}/${userAreas.length}` : 'Aprovar'}
                        </button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button data-testid={`reject-${u.id}`}
                              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-xs font-semibold transition-all">
                              Rejeitar
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Rejeitar voluntario</AlertDialogTitle>
                              <AlertDialogDescription>Tem certeza que deseja rejeitar <strong>{u.nome}</strong>? O cadastro sera removido.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleReject(u.id, u.nome)} className="bg-red-500 hover:bg-red-600">Rejeitar</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>

                    {/* Area selection for multi-area volunteers */}
                    {userAreas.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <p className="text-[10px] text-gray-500 font-semibold uppercase mb-1.5">
                          {hasMultipleAreas ? 'Aprovar por area (desmarque as que deseja recusar):' : 'Area solicitada:'}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {userAreas.map(area => {
                            const isSelected = selectedForUser.includes(area);
                            return (
                              <button
                                key={area}
                                onClick={() => hasMultipleAreas ? toggleApprovedArea(u.id, area, userAreas) : null}
                                data-testid={`area-toggle-${u.id}-${area}`}
                                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all border ${
                                  isSelected
                                    ? 'bg-green-100 text-green-800 border-green-300'
                                    : 'bg-red-50 text-red-400 border-red-200 line-through'
                                } ${hasMultipleAreas ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                              >
                                {areaLabels[area] || area}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Modal/Form */}
        {showForm && (
          <Card className="bg-white rounded-2xl p-6 shadow-lg border border-[#E1ECE5]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-nunito font-bold text-xl text-[#1A4D2E]">{editingUser ? 'Editar Usuário' : 'Novo Usuário'}</h3>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Nome *</label>
                <input data-testid="user-nome" value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent outline-none" placeholder="Nome completo" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
                <input data-testid="user-email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent outline-none" placeholder="email@exemplo.com" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Celular</label>
                <input data-testid="user-celular" value={form.celular} onChange={e => setForm(p => ({ ...p, celular: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent outline-none" placeholder="(15) 99999-9999" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">{editingUser ? 'Nova Senha (deixe vazio para manter)' : 'Senha *'}</label>
                <div className="relative">
                  <input data-testid="user-password" type={showPassword ? 'text' : 'password'} value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm pr-10 focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent outline-none" placeholder="Min. 6 caracteres" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Role Selection */}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-600 mb-2">Tipo de Usuário</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {ROLES.map(r => (
                  <button key={r.key} data-testid={`role-${r.key}`} onClick={() => handleRoleChange(r.key)}
                    className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl text-xs font-semibold border transition-all ${form.role === r.key ? `${r.color} text-white border-transparent` : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}>
                    <r.icon className="w-5 h-5" />
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Permissions */}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-600 mb-2">Permissões de Acesso</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {ALL_PERMISSIONS.map(p => (
                  <label key={p.key} className={`flex flex-col gap-1 px-3 py-3 rounded-xl border cursor-pointer transition-all ${form.permissions.includes(p.key) ? 'bg-[#F2F7F4] border-[#1A4D2E]' : 'bg-white border-gray-200 hover:border-gray-300'}`}>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={form.permissions.includes(p.key)} onChange={() => togglePermission(p.key)} className="accent-[#1A4D2E] w-4 h-4" />
                      <span className="font-inter text-sm font-semibold text-[#1A4D2E]">{p.label}</span>
                    </div>
                    <span className="font-inter text-[10px] text-gray-400 pl-6">{p.desc}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={resetForm} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancelar</button>
              <button onClick={handleSubmit} data-testid="save-user-btn" className="bg-[#1A4D2E] hover:bg-[#163f25] text-white px-6 py-2 rounded-lg text-sm font-semibold transition-all">
                {editingUser ? 'Salvar Alterações' : 'Criar Usuário'}
              </button>
            </div>
          </Card>
        )}

        {/* Users List */}
        {users.map(user => (
          <Card key={user.id} className="bg-white rounded-xl p-4 shadow-sm border border-[#E1ECE5] hover:shadow-md transition-all" data-testid={`user-card-${user.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-nunito font-bold text-lg text-[#1A4D2E]">{user.nome || 'Sem nome'}</h4>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                    user.role === 'admin' ? 'bg-[#1A4D2E] text-white' :
                    user.role === 'lider' ? 'bg-blue-600 text-white' :
                    user.role === 'responsavel' ? 'bg-amber-100 text-amber-700' :
                    'bg-[#5A7A3B]/10 text-[#5A7A3B]'
                  }`}>
                    {user.role === 'admin' ? 'Admin' : user.role === 'lider' ? 'Líder' : user.role === 'responsavel' ? 'Pais/Resp.' : 'Voluntário'}
                  </span>
                  {user.email === 'admin@sementes.com' && (
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Principal</span>
                  )}
                </div>
                <p className="font-inter text-sm text-gray-500">{user.email || user.celular || 'Sem contato'}</p>
                {(user.permissions || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(user.permissions || []).map(p => {
                      const permDef = ALL_PERMISSIONS.find(ap => ap.key === p);
                      return <span key={p} className="bg-gray-100 text-gray-500 px-2 py-0.5 rounded text-[10px]">{permDef?.label || p}</span>;
                    })}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openEdit(user)} data-testid={`edit-user-${user.id}`} className="text-gray-400 hover:text-[#1A4D2E] hover:bg-[#F2F7F4] rounded-full p-2 transition-all">
                  <Pencil className="w-4 h-4" />
                </button>
                {user.email !== 'admin@sementes.com' && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button data-testid={`delete-user-${user.id}`} className="text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full p-2 transition-all">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Excluir usuário</AlertDialogTitle>
                        <AlertDialogDescription>Tem certeza que deseja excluir <strong>{user.nome}</strong>? Esta ação não pode ser desfeita.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(user.id)} className="bg-red-500 hover:bg-red-600">Excluir</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>
          </Card>
        ))}

        {users.length === 0 && (
          <Card className="bg-white rounded-2xl p-12 text-center shadow-md">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="font-inter text-gray-500">Nenhum usuário cadastrado</p>
          </Card>
        )}
      </div>

      <Navigation active="" />
    </div>
  );
};

export default GestaoUsuarios;
